import { Typography, message as antdMessage } from 'antd';
import React, { useCallback, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';

import ErrorBoundaryFallback from 'components/atoms/ErrorBoundaryFallback';
import HelmetWrapper from 'components/atoms/HelmetWrapper';
import PageHeaderPusher from 'components/atoms/PageHeaderPusher';
import DefaultPageHeader from 'components/composites/DefaultPageHeader';
import LoadingSpinner from 'components/composites/LoadingSpinner';
import FarmbotAgriWebbIntegrationTitle from 'components/features/integrations/FarmbotAgriWebbIntegrationTitle';
import AgriWebbSiteFarmMappingForm from 'components/forms/AgriWebbSiteFarmMappingForm';
import AgriWebbFarmsRequest from 'components/requests/AgriWebbFarmsRequest';
import AgriWebbActionType from 'store/modules/agriWebb/constants';
import { AgriWebbFarm } from 'store/modules/agriWebb/types';
import { makeSelectFirstErrorMessage } from 'store/modules/error/selectors';
import { makeSelectIsLoading } from 'store/modules/loading/selectors';
import { makeSelectMyUserMembership } from 'store/modules/myUser/selectors';
import { AgriWebbSiteIntegrations } from 'types/models/agriwebb';
import { UserRole } from 'types/models/membership';
import getAgriWebbMarketplaceCardUrl from 'utils/agriwebb/get-agriwebb-marketplace-card-url';

import './styles.less';

const actions = [AgriWebbActionType.LOAD_FARMS_REQUEST];
const selectIsLoading = makeSelectIsLoading(actions);
const selectError = makeSelectFirstErrorMessage(actions);

export default function SiteFarmMappingAgriWebb() {
  const searchParams = new URLSearchParams(document.location.search);
  const [agriWebbFarms, setAgriWebbFarms] = useState<AgriWebbFarm[]>();
  const [existingMappings, setExistingMappings] = useState<AgriWebbSiteIntegrations[]>();
  const isLoading = useSelector(selectIsLoading);
  const error = useSelector(selectError);

  const enterpriseId: string | null = searchParams.get('enterpriseId');

  // Note: this use case is slightly different from the `useIsEnterpriseAdminOrSupervisor` hook
  // which checks myUser's role against the current enterprise domain. In AgriWebb routes, we do
  // not have an enterprise domain, so we must specify the exact enterprise to check against.
  const myUserMembership = useSelector(makeSelectMyUserMembership(Number(enterpriseId)));
  const canEdit = myUserMembership?.role === UserRole.ADMIN || myUserMembership?.role === UserRole.SUPERVISOR;

  const onDataFetchSuccess = useCallback((farms: AgriWebbFarm[], mappings: AgriWebbSiteIntegrations[]) => {
    setAgriWebbFarms(farms);
    setExistingMappings(mappings);
  }, []);

  if (!enterpriseId) {
    return <>Missing search params. Unable to proceed</>;
  }

  const handleSuccess = () => {
    antdMessage.success(
      'Your enterprise is integrated. Will redirect back to AgriWebb in 3s',
      3,
      () => {
        window.location.href = getAgriWebbMarketplaceCardUrl();
      }
    );
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <HelmetWrapper section="Connections" />
      <DefaultPageHeader title="Connections" />
      <AgriWebbFarmsRequest
        enterpriseId={+enterpriseId}
        onSuccess={onDataFetchSuccess}
      />
      {isLoading && <LoadingSpinner />}
      <PageHeaderPusher>
        <>
          {!!agriWebbFarms?.length && !!existingMappings?.length && (
            <div className="SiteFarmMappingAgriWebb">
              <FarmbotAgriWebbIntegrationTitle>
                {canEdit
                  ? 'Map your sites to AgriWebb farms:'
                  : 'View your site-to-farm mapping:'
                }
              </FarmbotAgriWebbIntegrationTitle>
              <AgriWebbSiteFarmMappingForm
                agriWebbFarms={agriWebbFarms}
                enterpriseId={+enterpriseId}
                existingMappings={existingMappings}
                onSuccess={handleSuccess}
                readOnly={!canEdit}
              />
            </div>
          )}
          {!agriWebbFarms?.length && !isLoading && (
            <Typography.Title level={5}>
              Sorry, no farms found.
            </Typography.Title>
          )}
          {error && !isLoading && (
            <Typography.Title level={5}>
              Sorry, something went wrong.
            </Typography.Title>
          )}
        </>
      </PageHeaderPusher>
    </ErrorBoundary>
  );
}
